import React, { useState, useEffect, forwardRef, useImperativeHandle} from "react";
import {
  Upload,
  message,
  Modal,
  Tooltip,
  Button,
  Spin,
  Empty
} from "antd";
import { LeftOutlined, RightOutlined, DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import './customUploadNew.scss'

import commonFn from "@/utils/commonFn";
import { acceptDict } from "@/utils/dict";

const CustomUploadNew1 = forwardRef(({
  filePaths,
  setFilePaths,
  btnDisabled,
  setBtnDisabled,
  customConfig,
}, ref) => {
  const [uploadFileList, setUploadFileList] = useState([]);
  const [fileIndex, setFileIndex] = useState(-1);
  const [refreshKey, setRefreshKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useImperativeHandle(ref, () => ({
    initUpdateList: (list) => {
      setUploadFileList(list)
      setFilePaths(list);
      setFileIndex(0)
      setBtnDisabled(false)
      setIsLoading(false)
      setRefreshKey(+new Date())
    },
    initIsLoading: flag => {
      setIsLoading(flag)
    },
    updateDocFn: () => {
      setRefreshKey(+new Date())
    }
  }));

  const getBase64 = (file) =>{
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    })
  };

  const deleteFn = async(file, fileList, index) => {
    if(customConfig?.deleteOldFn && file?.id){
      let flag = false
      Modal.confirm({
        title: "Delete File",
        content: "Do you want to delete this file?",
        okText: "Confirm",
        cancelText: "Cancel",
        onOk: async() => {
          flag = await customConfig.deleteOldFn(file)
          if(flag) {
            message.success("Successfully Deleted");
            if(fileIndex === index){
              setFileIndex(0)
            }
            if(fileIndex > index){
              setFileIndex(fileIndex - 1)
            }
            if(customConfig?.deleteBase64FileFn){
              const list = await customConfig?.deleteBase64FileFn(file)
              setUploadFileList(list)
              setFilePaths(list);
            } else {
              const list = filePaths.filter(item => item.uid !== file.uid)
              setUploadFileList(list)
              setFilePaths(list);
            }
          }else{
            message.warning("Failed Deleted");
          }
        },
        onCancel: () => {},
      });
      return

    }else{
      if(fileIndex === index){
        setFileIndex(0)
      }
      if(fileIndex > index){
        setFileIndex(fileIndex - 1)
      }
      if(customConfig?.deleteBase64FileFn){
        const list = await customConfig?.deleteBase64FileFn(file)
        setUploadFileList(list)
        setFilePaths(list);
      } else {
        const list = filePaths.filter(item => item.uid !== file.uid)
        setUploadFileList(pre => list)
        setFilePaths(pre => list);
      }
    }
  }

  const isFileAccepted = (file) => {
    const defaultAccept = ["application/pdf"];

    // 使用配置的 accept 类型或默认值
    const acceptList = customConfig?.accept
      ? customConfig.accept.split(",").map(item => item.trim())
      : Object.keys(acceptDict).filter(key => defaultAccept.includes(acceptDict[key]));

    const mappedAcceptTypes = acceptList.map(type => acceptDict[type] || type);

    const isAccepted = mappedAcceptTypes.includes(file?.type || file?.contentType);

    if (!isAccepted) {
      // 找到 file.type 对应的 key，用于提示用户
      // const fallbackKey = Object.keys(acceptDict).find(key => acceptDict[key] === file.type);
      // const ext = fallbackKey?.split(".")[1]?.toUpperCase() || 'file';
      const ext = acceptList.join(',').replaceAll('.', ' ').toUpperCase() || 'file';
      message.error(`Please upload supported ${ext} file!`);
    }

    return isAccepted;
  }

  const isImageFlagFn = (file) => {
    let fileUrl = file?.allPath
    if (!fileUrl) return false;
    const fileUrlSuffix = customConfig?.suffixKey ? file[customConfig?.suffixKey] : fileUrl?.split('?')?.[0]?.split('.')?.pop()?.toLowerCase()
    if (!fileUrlSuffix) return false;
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(fileUrlSuffix)) {
      return true
    }
  }

  const getViewerUrl = (file) => {
    let fileUrl = file?.allPath
    if (!fileUrl) return "";
    const fileUrlSuffix = customConfig?.suffixKey ? file[customConfig?.suffixKey] : 
    (customConfig?.fileFullName ? file?.[customConfig?.fileFullName] : fileUrl)?.split('?')?.[0]?.split('.')?.pop()?.toLowerCase()
    if (!fileUrlSuffix) return "";
    if (["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"].includes(fileUrlSuffix)) {
      return `/image-viewer.html?src=${encodeURIComponent(fileUrl)}`;
    }

    if (["pdf"].includes(fileUrlSuffix)) {
      return fileUrl;
    }

    return fileUrl;
  };

  const uploadChangeFn = async({ file, fileList, index}) => {
    // 删除
    if(file.status === "removed"){
      deleteFn(file, fileList, index)
      return
    }

    if (!isFileAccepted(file)) return;
    if(fileList[fileList.length - 1]?.uid === file.uid){
      if(customConfig?.addOldFn){
        const addOldData = await customConfig.addOldFn(fileList)
        if(addOldData?.flag) {
          message.success("Upload Successfully");
          setUploadFileList(prev => [...prev, {...fileList[fileList.length - 1], id: addOldData?.fileData?.id, uuid: addOldData?.fileData?.uuid}])
          setFilePaths(prev => [...prev, addOldData?.fileData]);
          setBtnDisabled(false)
          setFileIndex(filePaths?.length || 0)
        }else{
          message.warning("Failed Upload");
        }
        return
      }
      const newFileListTemp = fileList.filter((item) => filePaths.every((i) => i.uid !== item.uid));
      addUpload(newFileListTemp)
    }
  };

  //添加时上传
  const addUpload = async (data) => {
    const temp = await Promise.all(
      data.map(async item => ({
        ...item,
        baseUrl: await getBase64(item.originFileObj),
      }))
    );
    const fileList = [...uploadFileList, ...temp]
    
    setUploadFileList(fileList)
    setBtnDisabled(true)
    const uploadedFiles = await commonFn.uploadFilesNew(temp, api.contractUploadFile);
    if(!uploadedFiles?.length) return setBtnDisabled(false)
    const list = [...filePaths, ...uploadedFiles]
    setFilePaths(list);
    setBtnDisabled(false)
    setFileIndex(filePaths?.length || 0)
    setRefreshKey(+new Date())
  };

  const initFn = () => {
    setUploadFileList([])
    setFilePaths([])
    setFileIndex(-1)
    setBtnDisabled(false)
  }

  useEffect(()=>{
    return () =>{
      initFn()
    }
  },[])

  return (
    <>
      {isLoading ? 
        <div style={{width: '100%', height: '50vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
          <Spin tip="Loading" size="large">
            <div style= {{ padding: 50 }} />
          </Spin> 
        </div>
        :
      (<div className="customUploadNew">
        <Upload
            key={refreshKey}
            accept={customConfig?.accept || ".pdf"}
            listType={customConfig?.listType || "text"}
            fileList={uploadFileList}
            onChange={uploadChangeFn}
            beforeUpload={() => false}  // 阻止文件上传
            multiple={!customConfig?.notMultiple}
            showUploadList={false}
        >
            {uploadFileList?.length >= (customConfig?.maxLength || 10000) || customConfig?.noUploadBtn ? null : (
                <Button
                  disabled={customConfig?.disabled || btnDisabled}
                  icon={<UploadOutlined />}
                  type="primary"
                  style={{
                    // backgroundColor: '#2463ea', 
                    // color:'#fff', 
                    position: 'absolute', 
                    top: '-45px', 
                    right: 0,
                    ...(customConfig?.uploadButtonStyle || {})
                  }}
                >  
                  Upload File
                </Button>
              )
            }
        </Upload>

        {filePaths.length && (
          <>
            <div className="fileNameList" style={{ height: customConfig?.pdfHeight || "50vh" }}>
              {filePaths.map((item, index) => (
                  <Tooltip placement="top" title={item?.name || item?.fileName}>
                    <div className={`fileNameItem ${fileIndex === index && 'isSelect'}`}>
                      <a
                        className="fileNameItemTitle"
                        key={index}
                        onClick={() => setFileIndex(index)}
                      >
                        {item?.name || item?.fileName}
                      </a>
                      {customConfig?.noDeleteBtn ? null : <span className="fileNameItemIcon" onClick={() => uploadChangeFn({file: {...filePaths[index], status: "removed"}, fileList: filePaths, index})}>
                        <DeleteOutlined style={{fontSize: 16}} />
                      </span>}
                    </div>
                  </Tooltip>
                ))
              }
            </div>
            <div className="fileListbox">
              <iframe
                src={getViewerUrl(filePaths[fileIndex])}
                title="File Viewer"
                allowfullscreen="true"
                width="100%"
                style={{ height: customConfig?.pdfHeight || "50vh", pointerEvents: isImageFlagFn(filePaths[fileIndex]) ? 'none' : 'auto' }}
              />
              {fileIndex > 0 && <div className="arrow leftIcon" onClick={() => setFileIndex(fileIndex - 1)}>
                <LeftOutlined />
              </div>}
              {(fileIndex < filePaths?.length - 1 )&& <div className="arrow rightIcon" onClick={() => setFileIndex(fileIndex + 1)}>
                <RightOutlined />
              </div>}
            </div>
          </>
        ) || (
          !isLoading ? (
            <div style={{width: '100%', height: '50vh', display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
              <Empty description="No attachment" />
            </div>
          ) : null
        )}
      </div>)}
    </>
  );
});

export default CustomUploadNew1;